'use client';

/**
 * Global Confirmation Dialog Provider
 * Provides a theme-consistent confirmation modal throughout the app
 */

import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ReactNode } from 'react';

import { X, AlertTriangle, Info, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { registerConfirmDialog } from '@/lib/notifications';
import { isCapacitor } from '@/lib/platformUtils';

// Confirmations must sit above takeover screens — in-call UI / ringing UI use
// z-[9999] and clock/biometric/voting modals go up to z-[10050] — but stay below
// BiometricLockScreen (z-[99999]) so it can still gate an open dialog.
const CONFIRM_DIALOG_OVERLAY_Z_CLASS = 'z-[99997]';
const CONFIRM_DIALOG_FULLSCREEN_OVERLAY_Z_CLASS = 'z-[99998]';

type ConfirmDialogOptions = Parameters<Parameters<typeof registerConfirmDialog>[0]>[0];

type ConfirmDialogContextType = {
  showConfirm: (options: ConfirmDialogOptions) => void;
};

const ConfirmDialogContext = createContext<ConfirmDialogContextType | null>(null);

export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return context;
};

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [portalReady, setPortalReady] = useState(false);

  const showConfirm = React.useCallback((opts: ConfirmDialogOptions) => {
    setOptions(opts);
    setConfirmInput('');
    setConfirmChecked(false);
    setIsOpen(true);
  }, []);

  // Register the callback when component mounts and when showConfirm changes
  React.useEffect(() => {
    registerConfirmDialog(showConfirm);
    // Also register immediately in case confirm is called before useEffect runs
  }, [showConfirm]);

  React.useEffect(() => {
    setPortalReady(true);
    return () => {
      setPortalReady(false);
    };
  }, []);

  // Register synchronously on first render
  React.useMemo(() => {
    registerConfirmDialog(showConfirm);
  }, [showConfirm]);

  // Check if confirmation requirement is satisfied
  const getConfirmationValidationMessage = (): string | null => {
    if (!options?.requireConfirmation) return null;

    const {
      type,
      confirmValue,
      validator,
      minLength,
    } = options.requireConfirmation;

    if (type === 'checkbox') {
      return confirmChecked ? null : 'Please confirm before continuing.';
    }

    if (type === 'text' && confirmValue) {
      return confirmInput === confirmValue
        ? null
        : `Text does not match. Please type exactly: ${confirmValue}`;
    }

    const trimmedValue = confirmInput.trim();

    if ((type === 'input' || type === 'textarea') && minLength && trimmedValue.length < minLength) {
      return `Please enter at least ${minLength} characters before continuing.`;
    }

    if ((type === 'input' || type === 'textarea') && validator) {
      return validator(confirmInput);
    }

    return null;
  };

  const isConfirmationSatisfied = (): boolean => getConfirmationValidationMessage() == null;

  const handleConfirm = async () => {
    if (!options) return;

    // Prevent confirmation if validation requirements are not met
    if (!isConfirmationSatisfied()) {
      console.warn('[ConfirmDialog] Confirmation blocked - validation not satisfied');
      return;
    }

    setIsProcessing(true);
    try {
      await Promise.resolve(options.onConfirm?.({
        inputValue: confirmInput,
        checked: confirmChecked,
      }));
      setIsOpen(false);
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSecondaryConfirm = async () => {
    if (!options?.onSecondaryConfirm) return;

    // Prevent confirmation if validation requirements are not met
    if (!isConfirmationSatisfied()) {
      return;
    }

    setIsProcessing(true);
    try {
      await Promise.resolve(options.onSecondaryConfirm({
        inputValue: confirmInput,
        checked: confirmChecked,
      }));
      setIsOpen(false);
    } catch (error) {
      console.error('Secondary confirmation action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (options?.onCancel) {
      options.onCancel();
    }
    setIsOpen(false);
    setConfirmInput('');
    setConfirmChecked(false);
  };

  const getVariantStyles = () => {
    const variant = options?.variant || 'info';

    switch (variant) {
      case 'danger':
        return {
          icon: AlertTriangle,
          gradient: 'from-red-500 to-red-600',
          confirmButton: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        };
      case 'warning':
        return {
          icon: AlertCircle,
          gradient: 'from-amber-500 to-amber-600',
          confirmButton: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
        };
      case 'success':
        return {
          icon: CheckCircle,
          gradient: 'from-green-500 to-green-600',
          confirmButton: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
        };
      default:
        return {
          icon: Info,
          gradient: 'from-blue-500 to-blue-600',
          confirmButton: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        };
    }
  };

  const styles = getVariantStyles();
  const useFullScreenLayout = Boolean(
    options && (
      options.presentation === 'fullscreen'
      || (options.presentation === 'fullscreenOnCapacitor' && isCapacitor())
    )
  );

  const getSecondaryButtonStyle = () => {
    const variant = options?.secondaryVariant || 'danger';
    switch (variant) {
      case 'danger': return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'warning': return 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500';
      case 'success': return 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
      default: return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    }
  };

  const Icon = styles.icon;

  const dialogContent = isOpen && options ? (
    <div
      className={`fixed inset-0 ${useFullScreenLayout ? `${CONFIRM_DIALOG_FULLSCREEN_OVERLAY_Z_CLASS} items-stretch justify-stretch bg-black/75 p-0` : `${CONFIRM_DIALOG_OVERLAY_Z_CLASS} items-center justify-center bg-black/55 p-4`} flex animate-in fade-in duration-200`}
      // Radix Dialog applies `pointer-events: none` to <body> when open, and
      // since this overlay is portaled to <body> it inherits that and becomes
      // un-clickable. Force pointer events on so nested confirms work.
      style={{ pointerEvents: 'auto' }}
    >
      <div
        className={useFullScreenLayout
          ? 'h-[100dvh] max-h-[100dvh] w-full overflow-hidden bg-white shadow-2xl dark:bg-gray-900 animate-in fade-in duration-200 flex flex-col'
          : 'bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200'}
      >
        {/* ── Sticky Header ─────────────────────────────────────── */}
        <div
          className="border-b border-gray-200 dark:border-gray-700 shadow-sm flex-shrink-0 bg-white dark:bg-gray-800"
          style={useFullScreenLayout ? { paddingTop: 'max(env(safe-area-inset-top, 0px), 0px)' } : undefined}
        >
          <div className={`relative text-center ${useFullScreenLayout ? 'px-3 pb-3' : 'p-4'}`}
            style={useFullScreenLayout ? { paddingTop: 'max(env(safe-area-inset-top, 0px), 0.75rem)' } : undefined}
          >
            <button
              onClick={handleCancel}
              disabled={isProcessing}
              className="absolute right-3 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="text-center pt-1">
              <div className={`mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br text-white shadow-lg sm:mb-3 sm:h-14 sm:w-14 ${styles.gradient}`}>
                <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
                {options.title || 'Please confirm'}
              </h1>
            </div>
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────── */}
        <div className={`${useFullScreenLayout ? 'flex-1 overflow-y-auto px-4 py-4' : 'p-4'}`}>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{options.message}</p>

          {options.requireConfirmation && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              {options.requireConfirmation.type === 'checkbox' ? (
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {options.requireConfirmation.label || 'I understand this action cannot be undone'}
                  </span>
                </label>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {options.requireConfirmation.label || (options.requireConfirmation.type === 'text'
                      ? `Type "${options.requireConfirmation.confirmValue}" to confirm`
                      : 'Confirmation required')}
                  </label>
                  {options.requireConfirmation.type === 'textarea' ? (
                    <textarea
                      value={confirmInput}
                      onChange={(e) => setConfirmInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isConfirmationSatisfied()) {
                          e.preventDefault();
                        }
                      }}
                      placeholder={options.requireConfirmation.placeholder || ''}
                      rows={3}
                      className="w-full resize-y px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      autoFocus
                    />
                  ) : (
                    <input
                      type="text"
                      value={confirmInput}
                      onChange={(e) => setConfirmInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isConfirmationSatisfied()) {
                          e.preventDefault();
                        }
                      }}
                      placeholder={options.requireConfirmation.placeholder || options.requireConfirmation.confirmValue}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      autoFocus
                    />
                  )}
                  {getConfirmationValidationMessage() && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {getConfirmationValidationMessage()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Actions ─────────────────────────────────────────────── */}
        <div
          className={`flex gap-2 flex-shrink-0 ${useFullScreenLayout ? 'border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md px-3 pt-2.5 pb-3' : 'bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700'} ${options.secondaryConfirmText ? 'flex-col sm:flex-row' : ''}`}
        >
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {options.cancelText || 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing || !isConfirmationSatisfied()}
            className={`flex-1 px-3 py-2.5 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none text-sm ${styles.confirmButton}`}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin h-4 w-4" />
                Processing...
              </span>
            ) : (
              options.confirmText || 'Confirm'
            )}
          </button>
          {options.secondaryConfirmText && options.onSecondaryConfirm && (
            <button
              onClick={handleSecondaryConfirm}
              disabled={isProcessing || !isConfirmationSatisfied()}
              className={`flex-1 px-3 py-2.5 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none text-sm ${getSecondaryButtonStyle()}`}
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin h-4 w-4" />
                  Processing...
                </span>
              ) : (
                options.secondaryConfirmText
              )}
            </button>
          )}
        </div>

        {/* Bottom safe-area spacer (fullscreen) */}
        {useFullScreenLayout && (
          <div className="flex-shrink-0" style={{ height: 'max(env(safe-area-inset-bottom, 0px), 16px)' }} />
        )}
      </div>
    </div>
  ) : null;

  return (
    <ConfirmDialogContext.Provider value={{ showConfirm }}>
      {children}
      {portalReady && typeof document !== 'undefined'
        ? createPortal(dialogContent, document.body)
        : dialogContent}
    </ConfirmDialogContext.Provider>
  );
}
